/**
 * One project.
 *
 * The frame only: where you came from, the five figures that decide whether the
 * project is going well, and the tab strip. Each tab is its own file - they
 * share nothing but the project id, and keeping them here is how the section
 * grew to 839 lines in the first place. The delete dialog is a file of its own
 * for the same reason: it fetches its own counts and reads its own refusal out
 * of a 409, which is a job, not a decoration on this one. The head - the name,
 * the status field, the document menu and the four record-level actions - left
 * for `ProjectHead.tsx` when the archive control arrived, on the same grounds.
 *
 * The frame does own the two fetches, because they are the one thing no tab can
 * own for itself: the record and the summary are separate endpoints, and each
 * tab invalidates a different one. Handing every tab the same "reload
 * everything" is what made ticking a milestone refetch the project and reset the
 * milestone tab's own list under the operator's hand. See `reloadProject` and
 * `reloadSummary`.
 */
import { Suspense, lazy, useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, FolderKanban, Loader2, RefreshCw, TriangleAlert } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform } from "../../../lib/auth";
import {
  getProject, getProjectSummary, updateProject,
  type Milestone, type Project, type ProjectStatus, type ProjectSummary,
} from "../../../lib/projects";
import { onPinsChanged } from "../../../lib/projectPins";
import { useCrmUser } from "../../shell/CrmUser";
import { PopHost } from "../../shell/Pop";
import { Btn, Card, EmptyState, PillTabs, fmtNum } from "../../ui";
import { ErrorBoundary } from "../../shell/ErrorBoundary";
import { EntityFiles } from "../_shared/EntityFiles";
import { DeleteProjectDialog } from "./DeleteProjectDialog";
import { EditProjectModal } from "./EditProjectModal";
import { Milestones } from "./Milestones";
import { ProjectContracts } from "./ProjectContracts";
import { ProjectExpenses } from "./ProjectExpenses";
import { ProjectHead } from "./ProjectHead";
import { ProjectInvoices } from "./ProjectInvoices";
import { ProjectOverview } from "./ProjectOverview";
import { ProjectTasks } from "./ProjectTasks";
import { ProjectTickets } from "./ProjectTickets";
import { ProjectVault } from "./ProjectVault";
import { ProjectActivity } from "./ProjectActivity";
import { ProjectRoadmap } from "./Roadmap";
import { ProjectGantt } from "./gantt";
import { TimeEntries } from "./TimeEntries";
import { fmtHours, money } from "./lib";
import { PROJECT_TABS, TAB_PARAM, isTab, tabCount, type Tab } from "./tabs";

// Lazy, unlike every other tab: the flow map brings a whole canvas editor with
// it, and a project page that never opens the tab should not download one.
const ProjectFlowMap = lazy(() => import("./flowmap").then((m) => ({ default: m.ProjectFlowMap })));

/** One figure from the project KPI strip. Same shape as the sales strip, so a
 *  number means the same thing wherever the operator meets it. */
function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: "lt" }) {
  return (
    <div className="crm-q">
      <span>
        <span className="crm-q-lab block">{label}</span>
        <span className={cn("crm-q-val block", tone === "lt" && "text-red-600")}>{value}</span>
      </span>
    </div>
  );
}

export function ProjectDetail() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [project, setProject] = useState<(Project & { milestones: Milestone[] }) | null>(null);
  const [summary, setSummary] = useState<ProjectSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  /**
   * Bumped when the head books a cost, and used as the expenses tab's `key`.
   *
   * The tab holds its own rows and its own paging window, and it has no way of
   * hearing that something outside it wrote an expense. Remounting is blunt, but
   * it is honest: the alternative is a list that silently omits the row the
   * operator just created, and the only thing the remount costs is a "show more"
   * window that was opened before a write that changed the set anyway.
   */
  const [expensesEpoch, setExpensesEpoch] = useState(0);

  /**
   * The open tab is in the address bar, not in state.
   *
   * "Look at the hours on this one" used to be unsendable: the tab reset to the
   * overview on every mount, so a link could point at a project but never at a
   * part of it. An unknown or missing value falls back to the overview rather
   * than rendering nothing - a mistyped parameter is a wrong guess, not a
   * reason to show a blank page.
   */
  /**
   * Whether this operator may read the credential vault at all.
   *
   * Read once here rather than inside the tab, because it decides two separate
   * things that must agree: whether the pill is in the strip, and whether a
   * `?tab=vault` typed or pasted into the address bar is honoured. A guard on
   * only the first would leave the tab reachable by URL - which is not a
   * disclosure, since the API refuses the list too, but it is a card that
   * renders an error where an operator was told the tab does not exist.
   */
  const canReadVault = userCanPlatform(useCrmUser(), "read:crm.vault");

  const asked = params.get(TAB_PARAM);
  const wanted: Tab = isTab(asked) ? asked : "overview";
  // A tab this operator cannot open reads as the overview, the same as a
  // mistyped one: a wrong guess in the URL is not a reason to draw a blank page.
  const tab: Tab = wanted === "vault" && !canReadVault ? "overview" : wanted;

  const setTab = useCallback(
    (next: Tab) => {
      // `replace`, because the tabs are views of ONE page and not places you
      // have been: pushing would mean leaving a project you had clicked
      // through took as many Back presses as tabs you opened - which got worse
      // the moment the strip grew from six pills to nine. The other
      // parameters are carried over - a tab click must not drop a filter some
      // other screen put in the URL.
      setParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          sp.set(TAB_PARAM, next);
          return sp;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  /**
   * The record: the project's own fields and its milestone array.
   *
   * A failure keeps the project that is already on screen. It only leaves `null`
   * on the very first load, which is what the "not found" state below is for -
   * mid-session, a page that emptied itself because one GET timed out is worse
   * than one showing the row it had a second ago.
   */
  const reloadProject = useCallback(async () => {
    if (!id) return;
    const p = await getProject(id).catch(() => null);
    if (p) setProject(p);
  }, [id]);

  /**
   * The pin, unpinned from the sidebar.
   *
   * The rail draws this project as a row on every screen in the product,
   * including this one, and it can now drop the pin from there - so the badge in
   * the head would go on claiming a bookmark that no longer exists.
   *
   * The flag only, never `reloadProject`: the pin endpoints answer with the pin
   * and not with the project, and a refetch here would be a whole record and a
   * whole summary fetched to take a grey pill off the title row. Same reasoning
   * as `onPinChanged` further down, which this is the remote half of.
   */
  useEffect(
    () =>
      onPinsChanged(({ id: changed, pinned }) =>
        setProject((prev) => (prev && prev.id === changed ? { ...prev, pinned } : prev)),
      ),
    [],
  );

  /**
   * The derived numbers: every figure the server computes rather than stores.
   *
   * This is the one the TABS almost always want. Logging time, invoicing an
   * hour, moving a task and ticking a milestone all change money or task-derived
   * counts, and BOTH kinds live in this response - hours and euros beside the
   * task rollup and the effective percentage - so one refetch covers what looks
   * like two different invalidations. None of them touch the project record.
   *
   * Allowed to fail on its own, and a failure keeps the previous figures: it is
   * the KPI strip and the overview's cost card, and losing them must not take
   * the project page with them. Five stale figures beat none.
   */
  const reloadSummary = useCallback(async () => {
    if (!id) return;
    const s = await getProjectSummary(id).catch(() => null);
    if (s) setSummary(s);
  }, [id]);

  /** Both, for the first paint of a project - and for any caller that has
   *  genuinely invalidated the record and the numbers at once. */
  const reloadAll = useCallback(async () => {
    await Promise.all([reloadProject(), reloadSummary()]);
  }, [reloadProject, reloadSummary]);

  useEffect(() => {
    // Cleared rather than left standing: the route parameter can change under a
    // mounted component (a link from one project to another), and keeping the
    // old summary would print the previous project's figures under the new
    // project's name for as long as the fetch takes.
    setLoading(true);
    setProject(null);
    setSummary(null);
    void reloadAll().finally(() => setLoading(false));
  }, [reloadAll]);

  if (loading) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="crm-card">
        <EmptyState
          icon={FolderKanban}
          title={t("projects.notFound")}
          action={
            <Link to="/projects" className="crm-btn">
              <ChevronLeft className="size-4" />
              {t("projects.back")}
            </Link>
          }
        />
      </div>
    );
  }

  const p = project;

  async function changeStatus(s: ProjectStatus) {
    const next = await updateProject(p.id, { status: s });
    setProject((prev) => (prev ? { ...prev, status: next.status, completedAt: next.completedAt } : prev));
  }

  /**
   * The hand-typed percentage.
   *
   * The summary is mirrored rather than refetched, because the overview prints
   * `summary.progressPct` and a slider that only moved after a round trip would
   * feel broken. The mirror cannot lie: under "manual" the server's effective
   * figure IS the stored one, so writing the same number into both is what the
   * next GET would have returned anyway. Under "tasks" it would lie, which is
   * why the source is checked rather than assumed - the overview makes the
   * slider read-only there, but a guard beats a promise about another file.
   */
  async function changeProgress(v: number) {
    await updateProject(p.id, { progressPct: v });
    setProject((prev) => (prev ? { ...prev, progressPct: v } : prev));
    if (p.progressSource === "manual") {
      setSummary((prev) => (prev ? { ...prev, progressPct: v } : prev));
    }
  }

  /**
   * Who owns the percentage: the operator, or the task list.
   *
   * No `progressPct` goes with this PATCH, on purpose. The server keeps the
   * stored number untouched while the source is "tasks", which is the whole
   * reason switching back to "manual" restores what the operator typed instead
   * of freezing whatever the tasks happened to say. Sending a percentage here
   * would overwrite that number with the derived one and quietly destroy it.
   *
   * The summary is refetched rather than recomputed here. Which figure becomes
   * the effective one under each setting is the summary endpoint's rule, and a
   * second copy of it in this file is a copy that drifts.
   */
  async function changeProgressSource(s: Project["progressSource"]) {
    const next = await updateProject(p.id, { progressSource: s });
    setProject((prev) => (prev ? { ...prev, progressSource: next.progressSource } : prev));
    await reloadSummary();
  }

  /**
   * The edit modal answers with the saved row, so the head and the facts card
   * take it directly rather than paying for a second GET - which is why this
   * does not call `reloadAll`. The milestones are carried over by hand: PATCH
   * does not include them, and spreading a project without them over one with
   * them would empty the tab.
   *
   * The summary IS refetched, because budget and hourly rate are its inputs -
   * leaving the strip alone would show "spent" and "budget used" computed from
   * the numbers that were just replaced.
   */
  async function applyEdit(next: Project) {
    setProject((prev) => (prev ? { ...prev, ...next, milestones: next.milestones ?? prev.milestones } : prev));
    await reloadSummary();
  }

  // From the API, not from the rounded percentage. See `ProjectSummary.overBudget`.
  const overBudget = summary?.overBudget ?? false;

  // The strip IS the registry, in the registry's order. PillTabs wants a value
  // and a finished word; `PROJECT_TABS` carries the id and the key the word is
  // looked up by, so the order the operator sees cannot drift from the order the
  // icons are listed in - they are the same rows.
  //
  // The count is the registry's too (`tabCount`), read off the summary rather
  // than off anything a tab knows: the whole reason the summary carries the task
  // total, the milestone rollup and a count per linked register is so the strip
  // can say whether a tab is worth opening before anybody opens it. Absent while
  // the summary is still in flight, which is right - a badge that appeared as
  // "0" and then changed to "7" would have been wrong for as long as it was
  // legible - and absent again on an empty set, so a fresh project shows words
  // rather than a rail of zeroes.
  // The credentials tab is the one row of the registry that can be absent.
  // Reading the vault is `read:crm.vault` and it does not follow from
  // `manage:crm.projects`, so an operator without it gets eleven tabs and no
  // hint that a twelfth exists - rather than a pill that opens a card the API
  // answers with 403, which reads as a broken page instead of a closed door.
  const visibleTabs = PROJECT_TABS.filter((x) => x.id !== "vault" || canReadVault);

  const TABS = visibleTabs.map((x) => ({
    value: x.id,
    label: t(x.labelKey),
    count: tabCount(x.id, summary),
  }));

  // The row for the tab that is open. `tab` is already one of the table's ids by
  // the time it gets here, so the fallback answers the type checker rather than
  // the operator: `find` has no way to know the id came out of this same list. A
  // first row rather than a non-null assertion, because an assertion would keep
  // insisting long after somebody had made it untrue.
  const active = visibleTabs.find((x) => x.id === tab) ?? PROJECT_TABS[0];
  const Icon = active.icon;

  return (
    // One open menu at a time: the status field, the roadmap cards and the row
    // menus all draw from the same Pop bus.
    <PopHost>
      <Link to="/projects" className="crm-btn crm-btn-sm mb-3.5">
        <ChevronLeft className="size-4" />
        {t("projects.back")}
      </Link>

      <ProjectHead
        project={p}
        summary={summary}
        onStatus={(s) => void changeStatus(s)}
        onExpenseSaved={() => {
          // The tab is opened as well as refreshed: a cost booked from the
          // overview would otherwise save into a list the operator never sees,
          // which is indistinguishable from a save that did not happen.
          setExpensesEpoch((n) => n + 1);
          setTab("expenses");
          void reloadSummary();
        }}
        onEdit={() => setEditing(true)}
        onDelete={() => setConfirmDel(true)}
        // Only the archive stamp is taken from the response. The rest of the row
        // is the same record the page already holds, and the archive endpoints
        // answer WITHOUT the milestone array - spreading the whole thing over a
        // project that has one would empty the milestones tab, the same trap
        // `applyEdit` below spells out.
        onArchiveChanged={(next) =>
          setProject((prev) => (prev ? { ...prev, archivedAt: next.archivedAt } : prev))
        }
        // Only the flag, for the same reason only the archive stamp is taken
        // above: the pin endpoints answer with the pin and not with the project,
        // so there is no row to swap - and a partial project spread over one
        // that carries its milestone array would empty the milestones tab.
        onPinChanged={(pinned) => setProject((prev) => (prev ? { ...prev, pinned } : prev))}
      />

      {summary ? (
        // Five figures now, so the strip overrides the four-column default the
        // way the sales one does. The team and the last activity have fields
        // behind them since this ring, but they still stay OFF the strip and sit
        // in the head instead: both can be absent - every imported project has
        // no members at all - and a plate in a fixed grid cannot be left out, so
        // absence here would have to be a dash, which in a row of figures reads
        // as a number that failed to load rather than one nobody has recorded.
        // See `HeadMeta`, which simply gets shorter instead.
        <Card className="crm-quick" style={{ "--crm-quick-cols": 5 } as CSSProperties}>
          <Stat label={t("projects.budget")} value={money(summary.budgetCents ?? p.budgetCents)} />
          <Stat label={t("projects.spent")} value={money(summary.spentCents)} tone={overBudget ? "lt" : undefined} />
          <Stat label={t("projects.invoiced")} value={money(summary.invoicedCents)} />
          <Stat label={t("projects.loggedHours")} value={`${fmtHours(summary.totalMinutes)} h`} />
          <Stat
            label={t("projects.overdueTasks")}
            value={fmtNum(summary.overdueTasks)}
            tone={summary.overdueTasks > 0 ? "lt" : undefined}
          />
        </Card>
      ) : null}

      {/* A rail, not a wrapping row. Twelve tabs do not fit on one line on an
          ordinary laptop, and the ones that fell off sat under the strip looking
          like a second, unrelated group - so the strip keeps one line and
          scrolls, with the open tab pulled into view. See `TabRail`. */}
      <div className="crm-tools">
        <PillTabs rail value={tab} onChange={setTab} tabs={TABS} />
      </div>

      {/* The strip above says which project; this says which part of it, and it
          is the only place the tab's icon appears. Twelve glyphs in the strip
          would compete with the figures above it and read as decoration - worse
          now that the strip is a rail, where an icon on every pill costs width
          in the one row that has none to spare. The pills that DO carry a mark
          carry a number instead, which says something a glyph cannot. */}
      <h2 className="crm-card-t mb-3 mt-1 flex items-center gap-2 px-1.5">
        <Icon className="size-4" />
        {t(active.labelKey)}
      </h2>

      {/* Each tab is handed the reload for what IT can change. The milestone tab
          gets the summary and not the project on purpose: it keeps its own local
          list, seeded once, and reordering or ticking a row edits that list in
          place - a project refetch would drop a fresh array on top of it and
          reset the operator's work mid-gesture. What ticking a milestone really
          moves is the rollup, which is in the summary. The roadmap is the one
          tab that takes the project: dragging a card rewrites the milestone rows
          the other tabs are seeded from, and it holds no list the refetch could
          disturb. */}
      {tab === "overview" ? (
        <ProjectOverview
          project={p}
          summary={summary}
          onProgress={changeProgress}
          onProgressSource={(s) => void changeProgressSource(s)}
        />
      ) : null}
      {tab === "roadmap" ? <ProjectRoadmap project={p} onChange={() => void reloadProject()} /> : null}
      {/* The timeline gets no reload callback and needs none: it writes nothing.
          It fetches the milestone board itself rather than being handed the
          project's milestone array, because the array carries no tasks and a
          Gantt without tasks is a list of four phases. */}
      {tab === "gantt" ? <ProjectGantt project={p} /> : null}
      {/* The flow map loads and saves its own document, so it gets no reload
          callback: nothing it writes is in the record or the summary. The
          fallback is the same spinner card the page shows while it loads. */}
      {tab === "flow" ? (
        // Its own boundary: a map chunk that fails to load (a tab left open across a
        // deploy) must not take the project page down. React keeps a failed lazy
        // import, so the way back is a page reload, not a retry.
        <ErrorBoundary
          fallback={() => (
            <div className="crm-card">
              <EmptyState
                icon={TriangleAlert}
                title={t("flowMap.loadFailed")}
                action={
                  <Btn tone="dark" onClick={() => window.location.reload()}>
                    <RefreshCw className="size-4" />
                    {t("flowMap.reloadPage")}
                  </Btn>
                }
              />
            </div>
          )}
        >
          <Suspense
            fallback={
              <div className="crm-card flex justify-center py-16 text-muted-foreground">
                <Loader2 className="size-6 animate-spin" />
              </div>
            }
          >
            <ProjectFlowMap project={p} />
          </Suspense>
        </ErrorBoundary>
      ) : null}
      {tab === "tasks" ? <ProjectTasks projectId={p.id} onChange={() => void reloadSummary()} /> : null}
      {tab === "milestones" ? (
        <Milestones project={p} summary={summary} onChange={() => void reloadSummary()} />
      ) : null}
      {tab === "time" ? <TimeEntries projectId={p.id} onChange={() => void reloadSummary()} /> : null}
      {tab === "invoices" ? <ProjectInvoices summary={summary} /> : null}
      {/* The expenses tab gets the summary reload, because a cost booked here
          moves `spentCents` in the KPI strip and the expense count on its own
          pill - both of which live in that one response. It also gets the
          client, from the record the page already holds: an expense is refused
          by the API when its project and its client disagree, so the pair has to
          travel together rather than be looked up again on the way. */}
      {tab === "expenses" ? (
        <ProjectExpenses
          key={expensesEpoch}
          projectId={p.id}
          clientId={p.clientId}
          onChange={() => void reloadSummary()}
        />
      ) : null}
      {/* Both of these write only the project LINK, and the link is exactly what
          the count on their own pill is made of: attach a contract and the list
          under the tab shows it while the badge is still the number the summary
          was fetched with. So both get the summary reload, the same as the
          expenses tab. The contracts tab also gets the client, for the same
          reason the expenses tab does: its picker has to be narrowed to this
          project's client, and a tab that fetched that for itself would have its
          only write waiting on a request the page had already made. */}
      {tab === "contracts" ? (
        <ProjectContracts projectId={p.id} clientId={p.clientId} onChange={() => void reloadSummary()} />
      ) : null}
      {tab === "tickets" ? <ProjectTickets projectId={p.id} onChange={() => void reloadSummary()} /> : null}
      {/* The files tab is the shared `EntityFiles`, handed this project as its
          owner: a project's files and a task's files are the same rows in the
          same table read with a different `entity`, so there is one card for
          both (see the component's header). It takes its wording rather than
          choosing it - "no files on this project yet" is not the sentence a task
          wants - and the summary reload, because the count on its own pill comes
          out of that response like every other register's does. */}
      {tab === "files" ? (
        <EntityFiles
          entity="project"
          entityId={p.id}
          title={t("projects.files")}
          emptyBody={t("projects.noFiles")}
          onChange={() => void reloadSummary()}
        />
      ) : null}
      {/* The credentials. Handed the client as well as the project because a
          credential added here is filed under both: the vault's own register
          lists by client, and an entry that named only the project would be
          missing from the list an operator reaches for when they are asking
          "what do we hold for this customer". The summary reload is for the
          count on its own pill, like every register above.

          `tab` can only be "vault" when the permission is held - the guard is
          where `tab` is derived, not here - so this line does not repeat it. */}
      {tab === "vault" ? (
        <ProjectVault projectId={p.id} clientId={p.clientId} onChange={() => void reloadSummary()} />
      ) : null}
      {/* The timeline gets the summary reload for the same reason the registers
          above do - the count on its own pill comes out of that response - and
          for one more besides: an automatic entry is filed by a write that
          happened on ANOTHER tab, so the number is stale far more often here
          than anywhere else on this page. */}
      {tab === "activity" ? <ProjectActivity projectId={p.id} onChange={() => void reloadSummary()} /> : null}

      {editing ? (
        <EditProjectModal
          project={p}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setEditing(false);
            void applyEdit(next);
          }}
        />
      ) : null}

      {confirmDel ? (
        <DeleteProjectDialog
          projectId={p.id}
          onClose={() => setConfirmDel(false)}
          onDeleted={() => nav("/projects")}
        />
      ) : null}
    </PopHost>
  );
}
